import { m } from '$lib/paraglide/messages';

/**
 * Translates dynamic filter names and values from Shopify.
 *
 * Uses Paraglide messages with a naming convention:
 * - Filter names: filter_name_<Name> (e.g., filter_name_Gender)
 * - Filter values: filter_value_<Value> (e.g., filter_value_Men)
 *
 * Falls back to the original string if no translation exists.
 */

/**
 * Value aliases - maps variant values to their canonical form.
 * Used to merge duplicate filter values (e.g., "BL L" and "BLUE" are the same color).
 * Keys should be lowercase for case-insensitive matching.
 */
const VALUE_ALIASES: Record<string, string> = {
  'bl l': 'Blue',
  'bl_l': 'Blue',
};

/**
 * Normalize a filter value to its canonical form.
 * E.g., "BL L" → "Blue", "MEN" → "Men"
 */
export function normalizeFilterValue(value: string): string {
  const lower = value.toLowerCase().trim();
  if (VALUE_ALIASES[lower]) {
    return VALUE_ALIASES[lower];
  }
  // Title case the value for consistency
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/**
 * Get all raw values that should match a canonical filter value.
 * E.g., "Blue" → ["Blue", "BL L", "bl l", "BLUE", etc.]
 */
export function getFilterValueAliases(canonicalValue: string): string[] {
  const aliases = [canonicalValue];
  const canonicalLower = canonicalValue.toLowerCase();

  // Find all aliases that map to this canonical value
  for (const [alias, canonical] of Object.entries(VALUE_ALIASES)) {
    if (canonical.toLowerCase() === canonicalLower) {
      aliases.push(alias);
      aliases.push(alias.toUpperCase());
      aliases.push(alias.replace(/_/g, ' '));
      aliases.push(alias.replace(/_/g, ' ').toUpperCase());
    }
  }

  // Also add common case variations of the canonical value
  aliases.push(canonicalValue.toUpperCase());
  aliases.push(canonicalValue.toLowerCase());

  return [...new Set(aliases)]; // Remove duplicates
}

// Normalize string to valid message key: "HALF OPEN" → "Half_Open"
// Converts to Title_Case for consistent key matching
function toKey(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/(^|_)([a-z])/g, (_, prefix, char) => prefix + char.toUpperCase());
}

/**
 * Translate a filter name (e.g., "Gender" → "Φύλο" in Greek)
 */
export function translateFilterName(name: string): string {
  const key = `filter_name_${toKey(name)}`;
  const fn = (m as unknown as Record<string, (() => string) | undefined>)[key];
  return typeof fn === 'function' ? fn() : name;
}

/**
 * Translate a filter value (e.g., "Men" → "Ανδρικά" in Greek)
 */
export function translateFilterValue(value: string): string {
  const key = `filter_value_${toKey(value)}`;
  const fn = (m as unknown as Record<string, (() => string) | undefined>)[key];
  return typeof fn === 'function' ? fn() : value;
}
